import { LogManager } from '../../../utility/Logger';
import { AnchorResolver } from './AnchorResolver';

import type { Timeline } from '../Timeline';
import type {
  CropTransformKeyframe,
  IScene,
  LayoutOptions,
  LayoutStrategy,
  SceneRange,
  TrackTransforms,
} from './types';

const logger = LogManager.getLogger('LayoutStrategyResolver');

/**
 * LayoutStrategyResolver
 *
 * Pure stateless utility for resolving layout composition strategies.
 * Determines which scenes should be merged (keyframed) vs kept split
 * based on strategy type, scene count, and playback position.
 *
 * No store/service/renderer dependencies — fully testable with plain data.
 */
export class LayoutStrategyResolver {
  /**
   * For `hybrid` strategy, resolves to `'split'` or `'keyframe'` based on
   * scene count vs threshold. For `keyframe` and `split`, returns as-is.
   * Not used for `hybrid-adaptive` (which uses ranges instead).
   */
  static resolveEffectiveStrategy(
    strategy: LayoutStrategy,
    sceneCount: number,
    threshold: number,
  ): 'keyframe' | 'split' {
    if (strategy === 'keyframe') return 'keyframe';
    if (strategy === 'split') return 'split';

    // hybrid: split when below threshold, keyframe when at or above
    if (strategy === 'hybrid') {
      return sceneCount < threshold ? 'split' : 'keyframe';
    }

    // hybrid-adaptive should not use this method — callers use computeKeyframeRanges instead
    logger.warn(
      'resolveEffectiveStrategy called with hybrid-adaptive; falling back to hybrid behavior',
    );
    return sceneCount < threshold ? 'split' : 'keyframe';
  }

  /**
   * For `hybrid-adaptive`: computes scene index ranges that should have
   * keyframe merging applied. Scenes near `currentTime` stay split (no flash);
   * scenes outside the window get merged (performance).
   *
   * The split window is centered on the scene containing `currentTime` and
   * spans `threshold` scenes. Scenes outside that window are returned as
   * merge ranges.
   *
   * @returns SceneRange[] — ranges of scene indices to merge via keyframes.
   *   Empty array means no merging (all scenes stay split).
   *   Also returns the split window time bounds for caching in the store.
   */
  static computeKeyframeRanges(
    scenes: readonly IScene[],
    threshold: number,
    currentTime: number,
    timeline: Timeline,
  ): {
    mergeRanges: SceneRange[];
    splitWindow: { startTime: number; endTime: number };
  } {
    const anchorResolver = new AnchorResolver();
    const sceneCount = scenes.length;

    if (sceneCount === 0) {
      return {
        mergeRanges: [],
        splitWindow: this.clampWindowToPlayhead(
          { startTime: 0, endTime: 0 },
          currentTime,
        ),
      };
    }

    // If all scenes fit within the threshold, no merging needed
    if (sceneCount <= threshold) {
      const firstStart = anchorResolver.resolve(
        scenes[0].anchorStart,
        timeline,
      );
      const lastEnd = anchorResolver.resolve(
        scenes[sceneCount - 1].anchorEnd,
        timeline,
      );
      return {
        mergeRanges: [],
        splitWindow: this.clampWindowToPlayhead(
          { startTime: firstStart, endTime: lastEnd },
          currentTime,
        ),
      };
    }

    // Find the scene containing currentTime
    let currentSceneIndex = 0;
    for (let i = 0; i < sceneCount; i++) {
      const sceneEnd = anchorResolver.resolve(scenes[i].anchorEnd, timeline);
      if (currentTime < sceneEnd) {
        // find first scene that ends after currentTime — this is the current scene because scenes are checked in chronological order
        currentSceneIndex = i;
        break;
      }
      // If past all scenes, use the last one
      if (i === sceneCount - 1) {
        currentSceneIndex = i;
      }
    }

    // Start the split window at the current scene
    let splitStart = Math.max(0, currentSceneIndex);
    let splitEnd = Math.min(sceneCount - 1, splitStart + threshold - 1);

    // Adjust if we hit the end boundary
    if (splitEnd === sceneCount - 1) {
      splitStart = Math.max(0, sceneCount - threshold);
    }

    // Compute time bounds for the split window
    const splitStartTime = anchorResolver.resolve(
      scenes[splitStart].anchorStart,
      timeline,
    );
    const splitEndTime = anchorResolver.resolve(
      scenes[splitEnd].anchorEnd,
      timeline,
    );

    // Build merge ranges for scenes outside the split window
    const mergeRanges: SceneRange[] = [];
    if (splitStart > 0) {
      mergeRanges.push({ startIndex: 0, endIndex: splitStart - 1 });
    }
    if (splitEnd < sceneCount - 1) {
      mergeRanges.push({ startIndex: splitEnd + 1, endIndex: sceneCount - 1 });
    }

    const splitWindow = this.clampWindowToPlayhead(
      { startTime: splitStartTime, endTime: splitEndTime },
      currentTime,
    );

    logger.debug(
      `hybrid-adaptive: ${sceneCount} scenes, split window [${splitStart}..${splitEnd}] ` +
        `(${splitWindow.startTime.toFixed(2)}s-${splitWindow.endTime.toFixed(2)}s), ` +
        `merge ranges: ${mergeRanges.map(r => `[${r.startIndex}..${r.endIndex}]`).join(', ') || 'none'}`,
    );

    return { mergeRanges, splitWindow };
  }

  /**
   * Extend a scene-derived `[startTime, endTime]` window to include
   * `currentTime` if it sits outside.
   *
   * Scenes only cover regions of the timeline that contain video elements.
   * When non-video elements are prepended before the first scene, appended
   * after the last, or when scenes have gaps between them, the playhead can
   * sit in a region not covered by any scene. Without this clamp,
   * `needsHybridAdaptiveRefresh` would return `true` on every check (because
   * `currentTime` falls outside the scene-derived window), triggering an
   * infinite refresh storm in `AdaptivePlaybackSourceManager`.
   *
   * Clamping is safe: scene merging (`mergeRanges`) is unaffected, and
   * genuine cross-window transitions still trigger refresh because the
   * scene-bound side of the window is preserved.
   */
  private static clampWindowToPlayhead(
    window: { startTime: number; endTime: number },
    currentTime: number,
  ): { startTime: number; endTime: number } {
    return {
      startTime: Math.min(window.startTime, currentTime),
      endTime: Math.max(window.endTime, currentTime),
    };
  }

  /**
   * Identifies consecutive scenes that have identical positional properties
   * (same crop, scale, anchor across all bindings/keyframes). These scenes
   * produce no visual transition when merged, so they can always be safely
   * keyframe-merged regardless of strategy — no flash frames are possible.
   *
   * @returns SceneRange[] — ranges of scene indices with identical positions.
   */
  static findIdenticalPositionalSceneGroups(
    scenes: readonly IScene[],
    timeline: Timeline,
  ): SceneRange[] {
    if (scenes.length < 2) return [];

    const ranges: SceneRange[] = [];
    let groupStart = 0;

    for (let i = 1; i < scenes.length; i++) {
      if (
        LayoutStrategyResolver.scenesHaveIdenticalPositions(
          scenes[i - 1],
          scenes[i],
          timeline,
        )
      ) {
        // Continue the group
      } else {
        // Close the previous group if it had 2+ scenes
        if (i - 1 > groupStart) {
          ranges.push({ startIndex: groupStart, endIndex: i - 1 });
        }
        groupStart = i;
      }
    }

    // Close final group
    if (scenes.length - 1 > groupStart) {
      ranges.push({ startIndex: groupStart, endIndex: scenes.length - 1 });
    }

    return ranges;
  }

  /**
   * Merges two arrays of SceneRange into a single de-duplicated array.
   * Overlapping or adjacent ranges are combined.
   */
  static mergeSceneRanges(
    rangesA: SceneRange[],
    rangesB: SceneRange[],
  ): SceneRange[] {
    const all = [...rangesA, ...rangesB].sort(
      (a, b) => a.startIndex - b.startIndex,
    );
    if (all.length === 0) return [];

    const merged: SceneRange[] = [{ ...all[0] }];
    for (let i = 1; i < all.length; i++) {
      const last = merged[merged.length - 1];
      if (all[i].startIndex <= last.endIndex + 1) {
        // Overlapping or adjacent — extend
        last.endIndex = Math.max(last.endIndex, all[i].endIndex);
      } else {
        merged.push({ ...all[i] });
      }
    }
    return merged;
  }

  /**
   * Resolves the complete set of merge ranges for a given set of layout options.
   * This is the main entry point used by LayoutEngine.applyLayoutSegmentsToScenes.
   *
   * @returns mergeRanges to pass to mergeContiguousVideoChildren (undefined = merge all),
   *   and splitWindow for hybrid-adaptive caching.
   */
  static resolveLayoutPipeline(
    options: LayoutOptions,
    scenes: readonly IScene[],
    timeline: Timeline,
  ): {
    mergeRanges: SceneRange[] | undefined;
    splitWindow?: { startTime: number; endTime: number };
  } {
    const { strategy, sceneCount, sceneCountThreshold, currentTime } = options;

    // Identical-position groups are always safe to merge (no flash possible)
    const identicalGroups =
      LayoutStrategyResolver.findIdenticalPositionalSceneGroups(
        scenes,
        timeline,
      );

    if (strategy === 'keyframe') {
      // Merge everything — return undefined (signals "merge all" to LayoutEngine)
      return { mergeRanges: undefined };
    }

    if (strategy === 'split') {
      // Only merge identical-position groups
      return {
        mergeRanges: identicalGroups.length > 0 ? identicalGroups : [],
      };
    }

    if (strategy === 'hybrid') {
      const effective = LayoutStrategyResolver.resolveEffectiveStrategy(
        strategy,
        sceneCount,
        sceneCountThreshold,
      );
      if (effective === 'keyframe') {
        return { mergeRanges: undefined };
      }
      // split: only merge identical groups
      return {
        mergeRanges: identicalGroups.length > 0 ? identicalGroups : [],
      };
    }

    // hybrid-adaptive
    if (currentTime === undefined) {
      logger.warn(
        'hybrid-adaptive strategy requires currentTime; falling back to hybrid',
      );
      const effective = LayoutStrategyResolver.resolveEffectiveStrategy(
        'hybrid',
        sceneCount,
        sceneCountThreshold,
      );
      if (effective === 'keyframe') {
        return { mergeRanges: undefined };
      }
      return {
        mergeRanges: identicalGroups.length > 0 ? identicalGroups : [],
      };
    }

    const { mergeRanges, splitWindow } =
      LayoutStrategyResolver.computeKeyframeRanges(
        scenes,
        sceneCountThreshold,
        currentTime,
        timeline,
      );

    // Combine adaptive merge ranges with identical-position groups
    const combinedRanges = LayoutStrategyResolver.mergeSceneRanges(
      mergeRanges,
      identicalGroups,
    );

    return {
      mergeRanges: combinedRanges,
      splitWindow,
    };
  }

  /**
   * Compares two scenes to check if all their bindings have identical
   * positional properties (crop, scale, anchor).
   */
  private static scenesHaveIdenticalPositions(
    sceneA: IScene,
    sceneB: IScene,
    _timeline: Timeline,
  ): boolean {
    const bindingsA = sceneA.layout.bindings;
    const bindingsB = sceneB.layout.bindings;

    const slotsA = Object.keys(bindingsA).sort();
    const slotsB = Object.keys(bindingsB).sort();

    // Different slot sets — not identical
    if (slotsA.length !== slotsB.length) return false;
    if (slotsA.some((s, i) => s !== slotsB[i])) return false;

    // Compare each slot's transform keyframes
    for (const slot of slotsA) {
      if (
        !LayoutStrategyResolver.bindingsHaveIdenticalPositions(
          bindingsA[slot],
          bindingsB[slot],
        )
      ) {
        return false;
      }
    }
    return true;
  }

  /**
   * Compares two track bindings to check if they have identical positional keyframes.
   */
  private static bindingsHaveIdenticalPositions(
    a: TrackTransforms,
    b: TrackTransforms,
  ): boolean {
    const kfA = a.transformKeyframes;
    const kfB = b.transformKeyframes;

    if (kfA.length !== kfB.length) return false;

    for (let i = 0; i < kfA.length; i++) {
      if (
        !LayoutStrategyResolver.keyframesHaveIdenticalPosition(kfA[i], kfB[i])
      ) {
        return false;
      }
    }
    return true;
  }

  /**
   * Compares two crop transform keyframes for positional equality.
   */
  private static keyframesHaveIdenticalPosition(
    a: CropTransformKeyframe,
    b: CropTransformKeyframe,
  ): boolean {
    const EPSILON = 1e-6;

    return (
      Math.abs(a.crop.x - b.crop.x) < EPSILON &&
      Math.abs(a.crop.y - b.crop.y) < EPSILON &&
      Math.abs(a.crop.w - b.crop.w) < EPSILON &&
      Math.abs(a.crop.h - b.crop.h) < EPSILON &&
      Math.abs(a.scale - b.scale) < EPSILON &&
      Math.abs(a.anchor.x - b.anchor.x) < EPSILON &&
      Math.abs(a.anchor.y - b.anchor.y) < EPSILON
    );
  }
}
